/**
 * Famous tactics — pre-loaded reference scenarios drawn from coaching
 * literature so coaches can load a known system, tweak it, save it as
 * their own. Each entry is a `TacticsPattern` literal tagged
 * `sourceType: 'CURATED_EXPERT'` with a curator name.
 *
 * Each spec carries:
 *   - homeFormation / awayFormation — drives `projectFormation` for spots
 *   - homeTactics  — team-wide knobs (buildUpStyle, defensiveApproach, 6 sliders)
 *   - homeRoles    — slot-tag → named-role map (false-9, ball-playing,
 *                    pressing-forward, inverted-full-back, etc.).
 *
 * When the coach applies a pattern the brief engine sees the team
 * tactics + named roles and produces system-flavoured bullets (e.g.
 * Klopp's gegenpress flips the ST to a pressing-forward and the brief
 * reads "trigger the press on backwards passes", not the generic ST
 * baseline).
 *
 * Arrows / coachingPoints are still omitted in v1 — coaches author
 * their own movement on top of the loaded shape.
 */
import { projectFormation } from '../components/tactics3d/formationProjection';
import type {
  TacticsLayout,
  TacticsPlayerSpot,
} from '@4cm/core/tactics';
import {
  defaultBundle,
  type BuildUpStyle,
  type DefensiveApproach,
} from '@4cm/core/tactics/teamTactics';
import { applyTeamShapeTransform } from '@4cm/core/tactics/teamShape';
import {
  INSTRUCTION_FIELDS,
  type PlayerRoleSelection,
} from '@4cm/core/tactics/roles';
import type { TacticsPattern } from './tacticsPatternsStore';

const NOW = '2026-05-14T00:00:00.000Z';

interface HomeTacticsOverride {
  buildUpStyle: BuildUpStyle;
  defensiveApproach: DefensiveApproach;
  sliders: Partial<Record<
    'pressingIntensity' | 'width' | 'depth' | 'tempo' | 'aggression' | 'lineHeight',
    number
  >>;
}

/** Build a fully-defaulted `PlayerRoleSelection` carrying the given
 *  named role. Defaults come from `INSTRUCTION_FIELDS` so any future
 *  field is covered without a manual update here. */
function roleSelection(named: string): PlayerRoleSelection {
  const instructions: Record<string, string> = {};
  for (const f of INSTRUCTION_FIELDS) instructions[f.key] = f.default;
  return { role: named, instructions };
}

/** Compose the layout from a spec — projects spots, builds a
 *  teamTactics bundle with the home active slot patched, walks
 *  homeSpots to assign named roles by slot tag, then applies the
 *  spatial slider transform so loading the pattern visibly reshapes
 *  the team (Klopp's high line, Mourinho's low block, Conte's wide
 *  WBs all show on the pitch the moment the layout lands). */
function buildLayout(spec: FamousPatternSpec): TacticsLayout {
  const baseHome = projectFormation(spec.homeFormation, 'home') as TacticsPlayerSpot[];
  const awaySpots = projectFormation(spec.awayFormation, 'away') as TacticsPlayerSpot[];

  // teamTactics — clone the defaults then patch the active home slot.
  const bundle = defaultBundle();
  const homeActive = bundle.home.slots.find((s) => s.id === bundle.home.activeSlotId);
  if (homeActive) {
    homeActive.tactics.buildUpStyle = spec.homeTactics.buildUpStyle;
    homeActive.tactics.defensiveApproach = spec.homeTactics.defensiveApproach;
    for (const [k, v] of Object.entries(spec.homeTactics.sliders)) {
      if (typeof v === 'number') homeActive.tactics.sliders[k] = v;
    }
  }

  // Visual reshape from the spatial sliders — bakes the high-line /
  // narrow / stretched offsets into homeSpots so the board renders
  // the system's shape, not just the formation's baseline.
  const homeSpots = applyTeamShapeTransform(
    baseHome,
    homeActive?.tactics,
    'home',
  );

  // playerRoles — assign every home spot whose slot tag matches an
  // entry in spec.homeRoles. Spots without a mapped role just get the
  // slot baseline from the rule engine.
  const playerRoles: Record<string, PlayerRoleSelection> = {};
  for (let i = 0; i < homeSpots.length; i++) {
    const slot = homeSpots[i]!.role;
    const named = spec.homeRoles[slot];
    if (named) playerRoles[`home-${i}`] = roleSelection(named);
  }

  return {
    v: 5,
    homeFormation: spec.homeFormation,
    awayFormation: spec.awayFormation,
    homeSpots,
    awaySpots,
    arrows: [],
    teamTactics: bundle,
    playerRoles,
    saved_at: NOW,
  };
}

interface FamousPatternSpec {
  id: string;
  name: string;
  description: string;
  tags: string[];
  phaseOfPlay: TacticsPattern['phaseOfPlay'];
  homeFormation: string;
  awayFormation: string;
  curatorName: string;
  homeTactics: HomeTacticsOverride;
  /** Slot tag → named-role identifier. Slot tags are the same codes
   *  `projectFormation` emits (`GK`, `CB`, `LB`, `RB`, `LWB`, `RWB`,
   *  `CDM`, `CM`, `LM`, `RM`, `CAM`, `LW`, `RW`, `ST`). */
  homeRoles: Record<string, string>;
}

const SPECS: FamousPatternSpec[] = [
  {
    id: 'famous-cruyff-total-football',
    name: 'Cruyff — Total Football',
    description:
      "Ajax 1971-73, Barça '88-96. Positional fluidity; every outfielder competent in every role.",
    tags: ['cruyff', 'positional', 'total-football', 'attacking'],
    phaseOfPlay: 'attacking',
    homeFormation: '4-3-3',
    awayFormation: '4-4-2',
    curatorName: 'Johan Cruyff',
    homeTactics: {
      buildUpStyle: 'short-pass',
      defensiveApproach: 'high-press',
      sliders: { pressingIntensity: 70, width: 70, lineHeight: 75, tempo: 60, aggression: 60 },
    },
    homeRoles: {
      GK: 'sweeper-keeper',
      CB: 'ball-playing',
      LB: 'overlapping-full-back',
      RB: 'overlapping-full-back',
      CM: 'mezzala',
      LW: 'inside-forward',
      RW: 'inside-forward',
      ST: 'false-9',
    },
  },
  {
    id: 'famous-pep-tiki-taka',
    name: 'Pep — Tiki-Taka false 9',
    description:
      "Barça 2008-12. Possession-dominant 4-3-3 with Messi as the dropping false 9; build-up triangles via Xavi-Iniesta-Busquets.",
    tags: ['guardiola', 'tiki-taka', 'possession', 'false-9'],
    phaseOfPlay: 'attacking',
    homeFormation: '4-3-3',
    awayFormation: '4-4-2',
    curatorName: 'Pep Guardiola',
    homeTactics: {
      buildUpStyle: 'short-pass',
      defensiveApproach: 'counter-press',
      sliders: { pressingIntensity: 80, width: 55, lineHeight: 70, tempo: 70, aggression: 65 },
    },
    homeRoles: {
      GK: 'sweeper-keeper',
      CB: 'ball-playing',
      LB: 'overlapping-full-back',
      RB: 'overlapping-full-back',
      CM: 'advanced-playmaker',
      LW: 'inside-forward',
      RW: 'inside-forward',
      ST: 'false-9',
    },
  },
  {
    id: 'famous-pep-3-2-5-city',
    name: "Pep — 3-2-5 City build-up",
    description:
      "Man City 2022-24. Back three in build with inverted full-backs holding alongside the single pivot; wide forwards inverting into the half-spaces makes a 3-2-5.",
    tags: ['guardiola', '3-2-5', 'inverted-fullback', 'build-up'],
    phaseOfPlay: 'attacking',
    homeFormation: '4-3-3',
    awayFormation: '4-4-2',
    curatorName: 'Pep Guardiola',
    homeTactics: {
      buildUpStyle: 'short-pass',
      defensiveApproach: 'counter-press',
      sliders: { pressingIntensity: 75, width: 55, lineHeight: 70, tempo: 65, aggression: 60 },
    },
    homeRoles: {
      GK: 'sweeper-keeper',
      CB: 'ball-playing',
      LB: 'inverted-full-back',
      RB: 'inverted-full-back',
      CM: 'advanced-playmaker',
      LW: 'inside-forward',
      RW: 'inside-forward',
      ST: 'complete-forward',
    },
  },
  {
    id: 'famous-klopp-gegenpress',
    name: 'Klopp — Gegenpressing',
    description:
      "Liverpool 2015-22. Counter-press the moment possession is lost; 6-second window to win the ball back in the opposition half. Triggers: opponent back-pass, square pass, bad first touch.",
    tags: ['klopp', 'gegenpressing', 'counter-press', 'defending'],
    phaseOfPlay: 'defending',
    homeFormation: '4-3-3',
    awayFormation: '4-2-3-1',
    curatorName: 'Jürgen Klopp',
    homeTactics: {
      buildUpStyle: 'direct',
      defensiveApproach: 'counter-press',
      sliders: { pressingIntensity: 85, width: 55, lineHeight: 65, tempo: 75, aggression: 80 },
    },
    homeRoles: {
      GK: 'sweeper-keeper',
      CB: 'ball-playing',
      LB: 'overlapping-full-back',
      RB: 'overlapping-full-back',
      CM: 'box-to-box',
      LW: 'inside-forward',
      RW: 'inside-forward',
      ST: 'pressing-forward',
    },
  },
  {
    id: 'famous-conte-3-5-2',
    name: 'Conte — 3-5-2 wing-backs',
    description:
      "Chelsea 2016-17, Inter 2020-21. Back three plus aggressive wing-backs supplying width; two strikers occupy the centre-backs.",
    tags: ['conte', '3-5-2', 'wing-back', 'attacking'],
    phaseOfPlay: 'attacking',
    homeFormation: '3-5-2',
    awayFormation: '4-3-3',
    curatorName: 'Antonio Conte',
    homeTactics: {
      buildUpStyle: 'direct',
      defensiveApproach: 'mid-block',
      sliders: { pressingIntensity: 55, width: 75, tempo: 55, aggression: 65 },
    },
    homeRoles: {
      CB: 'stopper',
      LWB: 'wing-back',
      RWB: 'wing-back',
      CM: 'box-to-box',
      ST: 'target',
    },
  },
  {
    id: 'famous-mourinho-low-block',
    name: 'Mourinho — Parked-bus low block',
    description:
      "Inter 2010, Chelsea 2014-15. Deep 4-2-3-1 / 4-5-1; concede space, deny chances, win on transitions and set pieces.",
    tags: ['mourinho', 'low-block', 'defending', 'transitions'],
    phaseOfPlay: 'defending',
    homeFormation: '4-2-3-1',
    awayFormation: '4-3-3',
    curatorName: 'José Mourinho',
    homeTactics: {
      buildUpStyle: 'long-ball',
      defensiveApproach: 'low-block',
      sliders: { pressingIntensity: 25, width: 45, lineHeight: 25, tempo: 35, aggression: 55 },
    },
    homeRoles: {
      CB: 'stopper',
      CDM: 'holding-midfielder',
      LW: 'pure-winger',
      RW: 'pure-winger',
      CAM: 'shadow-striker',
      ST: 'target',
    },
  },
  {
    id: 'famous-de-zerbi-build',
    name: 'De Zerbi — Build-up baits',
    description:
      "Brighton 2022-24. Slow ball into back four to bait the press, lift cover-shadow defenders, then break lines vertically.",
    tags: ['de-zerbi', 'build-up', 'attacking', 'positional'],
    phaseOfPlay: 'attacking',
    homeFormation: '4-2-3-1',
    awayFormation: '4-4-2',
    curatorName: 'Roberto De Zerbi',
    homeTactics: {
      buildUpStyle: 'short-pass',
      defensiveApproach: 'high-press',
      sliders: { pressingIntensity: 70, width: 55, lineHeight: 65, tempo: 40, aggression: 55 },
    },
    homeRoles: {
      GK: 'sweeper-keeper',
      CB: 'ball-playing',
      LB: 'inverted-full-back',
      RB: 'inverted-full-back',
      CDM: 'deep-lying-playmaker',
      CAM: 'advanced-playmaker',
      LW: 'inside-forward',
      RW: 'inside-forward',
      ST: 'complete-forward',
    },
  },
  {
    id: 'famous-simeone-mid-block',
    name: 'Simeone — 4-4-2 mid-block',
    description:
      "Atlético Madrid 2012-22. Compact 4-4-2 between the lines; deny central progression; win the second ball.",
    tags: ['simeone', '4-4-2', 'mid-block', 'defending'],
    phaseOfPlay: 'defending',
    homeFormation: '4-4-2',
    awayFormation: '4-3-3',
    curatorName: 'Diego Simeone',
    homeTactics: {
      buildUpStyle: 'direct',
      defensiveApproach: 'mid-block',
      sliders: { pressingIntensity: 50, width: 55, lineHeight: 45, tempo: 50, aggression: 75 },
    },
    homeRoles: {
      CB: 'stopper',
      CM: 'ball-winner',
      LM: 'pure-winger',
      RM: 'pure-winger',
      ST: 'target',
    },
  },
];

/** Construct the full TacticsPattern[] from the specs. Built lazily on
 *  first call + cached, since `projectFormation` does some math. */
let cached: TacticsPattern[] | null = null;

export function getFamousTacticsPatterns(): TacticsPattern[] {
  if (cached) return cached;
  cached = SPECS.map((spec) => ({
    id: spec.id,
    name: spec.name,
    description: spec.description,
    tags: spec.tags,
    phaseOfPlay: spec.phaseOfPlay,
    formation: spec.homeFormation,
    layout: buildLayout(spec),
    sourceType: 'CURATED_EXPERT',
    curatorName: spec.curatorName,
    createdAt: NOW,
    updatedAt: NOW,
  }));
  return cached;
}

// Test-only — reset the lazy cache so a re-import or a vitest
// `vi.resetModules()` starts from a fresh build.
export function __resetFamousTacticsCache(): void {
  cached = null;
}
